// ─────────────────────────────────────────────────────────────────────────────
// REQ-112 — Documentos de la campaña con visor PDF modal.
//
// Datos REALES (useCampaignDocuments → GET v1/campaigns/{id}/documents). Los documentos se
// agrupan por categoría; al abrir uno se muestra un modal con una pestaña por idioma EN ORDEN
// DE CARGA (campo `orden`), abriendo por defecto el idioma del usuario si existe. Descarga directa.
//
// SECCIÓN OCULTA SI NO HAY NADA CONFIGURADO (revierte la decisión de 2026-08-03 de caer a un piso
// estático de 6 PDFs genéricos del prototipo). Cuando NINGÚN nivel de la resolución de abajo aporta
// documentos (ni excepción, ni campaña, ni corporativos — Tarea 16), el componente no pinta nada: no
// tiene sentido mostrar la sección "Documentos" con contenido que no es de esta OU/campaña. Los 6
// PDFs del prototipo siguen viviendo en `public/assets/docs/` y el riel derecho del borde
// (`HomeRightRail`) los sigue sirviendo ahí — solo se retiró el fallback DENTRO de esta sección.
//
// VISOR (2026-08-05) — el PDF se dibuja con `pdfjs-dist` sobre `<canvas>` (`PdfCanvasViewer`,
// `components/common/`, compartido con `HomeRightRail.tsx`), en vez de un `<iframe src={pdfUrl}>`.
// Un iframe apuntando directo a un PDF deja la decisión "mostrar inline vs. descargar" en manos de
// la preferencia de PDF del navegador (`chrome://settings/content/pdfDocuments`) o de una política
// corporativa (`AlwaysOpenPdfExternally`) — con "Descargar archivos PDF" activado, CUALQUIER
// iframe apuntando a un PDF dispara la descarga en vez de mostrarlo, sin que la app pueda evitarlo
// desde el HTML (probado: ni siquiera sirviéndolo como `blob:` cambia el comportamiento, porque el
// navegador igual reconoce el `Content-Type`). Ver `PdfCanvasViewer.tsx` para el detalle completo.
//
// R8b — RESOLUCIÓN (Tarea 15 + Tarea 16, SDD 2026-08-17; nivel 2 REVERTIDO 2026-08-18). Cuando el
// caller informa `programa` (el programa de la pantalla: Fundamentales/Brillamos/Personalizada) se
// intercala un nivel de excepción antes de la campaña:
//   1. Excepción ACTIVA para la OU del usuario CON documentos propios → esos, sin mezclar.
//   2. Excepción ACTIVA SIN documentos propios → OVERRIDE DURO: la sección se oculta, PUNTO. NO cae
//      a los documentos de la campaña base ni a los corporativos (ver 4). Antes (Tarea 15 original)
//      sí heredaba de la campaña base — revertido tras reporte del usuario (OU FOSE, verificado en
//      Mongo: excepción activa `Programa: "Brillamos"` sin documentos propios seguía mostrando
//      documentos de otra campaña/corporativos que no eran de esa localidad). Una excepción es la
//      personalización explícita por OU: si el admin no le cargó documentos, la lectura correcta es
//      "esta localidad no tiene documentos que mostrar", no "hereda los de otro lado".
//   3. Sin excepción activa, campaña CON documentos propios → esos (comportamiento de siempre).
//   4. Sin excepción activa (nunca si hay una activa, ver 2) y la campaña tampoco aporta nada →
//      documentos CORPORATIVOS globales (`GET v1/corporate-documents/for-me?lang=`, ya filtrados por
//      OU del usuario en el backend), mapeados de `CorporateDocType` a la categoría equivalente
//      (`CATEGORY_TO_CORPORATE_TYPE`). Si tampoco hay corporativos para esa OU, la sección NO se
//      pinta (ver nota arriba). Igual que los demás niveles, BLOQUEA el render mientras resuelve —
//      nunca pinta un estado "de paso" para luego sustituirlo un instante después.
//      EXCEPCIÓN (2026-08-18): el nivel 4 tampoco aplica cuando la campaña es Personalizada (prop
//      `isPersonalizada`, NO derivada de `programa` — ver comentario junto a `allowsCorporateFallback`
//      más abajo) — una campaña por localidad sin documentos propios oculta la sección en vez de
//      mostrar el repositorio corporativo genérico (no es específico de esa localidad). Sigue
//      aplicando a Fundamentales/Brillamos SIN excepción activa.
// Tarea 16 — decisión de negocio YA CONFIRMADA por el cliente: invierte a propósito la regla de
// "repositorios separados" documentada en el dominio backend (`CorporateDocument.cs`,
// RN-G10/RN-E06). Es intencional, no un bug (salvo las excepciones de Personalizada y de excepción
// activa arriba).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PdfCanvasViewer } from '@/components/common/PdfCanvasViewer';
import {
  useCampaignDocuments, useActiveCampaignException, useCampaignExceptionDocuments,
  useCorporateDocumentsForMe, pickLocalized,
} from '@/api/recognition';
import type {
  CampaignDocumentDto, CampaignType, CorporateDocumentForMeDto, CorporateDocType,
} from '@/api/recognition';

/** Emoji por categoría de documento (H-16/punto 16): 📋🏆⚖️🔒 son literales del demo
 * (BrillamosJuntosPage.tsx : 242-255, mismas 4 categorías howto/awards/legal/privacy). El
 * catálogo real de campaña admite más categorías que el demo no contempla (cookies/notice) —
 * se extiende con emojis del mismo lenguaje visual (regla 6: no se retira funcionalidad real). */
const CATEGORY_ICON: Record<string, string> = {
  howto: '📋',
  awards: '🏆',
  legal: '⚖️',
  privacy: '🔒',
  cookies: '🍪',
  notice: '📄',
};

function catIcon(cat: string): string {
  return CATEGORY_ICON[cat.toLowerCase()] ?? '📄';
}

interface DocGroup {
  categoria: string;
  /** Variantes por idioma, ordenadas por `orden` (orden de carga). */
  variants: CampaignDocumentDto[];
}

/**
 * WP-B1 · RN-C04 — «Si un PDF tiene múltiples idiomas activos pero un solo archivo, se muestra a
 * TODOS los idiomas seleccionados». Ese es exactamente el comodín `*`, que ya viaja en datos vivos
 * y hasta ahora no lo entendía nadie: `d.languageCode === lang` no casa jamás con `*`, así que el
 * documento se listaba pero nunca se elegía — y el visor abría en la primera variante, que podía
 * estar en un idioma que el usuario no habla.
 *
 * El orden importa: primero la coincidencia EXACTA, después el comodín. Un documento traducido a
 * un idioma concreto gana siempre al archivo genérico, que es el sentido de RN-C04 — el comodín es
 * el respaldo, no la preferencia.
 */
export const COMODIN_IDIOMA = '*';

export function variantePreferida(
  variants: ReadonlyArray<{ languageCode: string }>,
  lang: string,
): number {
  const exacta = variants.findIndex((d) => d.languageCode === lang);
  if (exacta >= 0) return exacta;
  const comodin = variants.findIndex((d) => d.languageCode === COMODIN_IDIOMA);
  return comodin >= 0 ? comodin : 0;
}

/** Modal visor con pestañas por idioma. */
function DocViewer({ group, lang, onClose }: Readonly<{ group: DocGroup; lang: string; onClose: () => void }>) {
  const { t } = useTranslation('recognition');
  // Pestaña por defecto: la del idioma del usuario si existe, si no la primera cargada.
  const [tab, setTab] = useState(() => variantePreferida(group.variants, lang));
  const current = group.variants[tab] ?? group.variants[0];
  const [loadError, setLoadError] = useState(false);
  // Reintentar automáticamente al cambiar de pestaña (idioma) o de documento — cada uno es un
  // archivo distinto, el error de uno no debe arrastrarse al siguiente.
  useEffect(() => { setLoadError(false); }, [current.url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = pickLocalized(current.titulo, lang) || t(`docs.cat.${group.categoria}`, group.categoria);

  // `createPortal` a `document.body`: `campaign-docs-section` (dueño de este modal) lleva
  // `position: relative; z-index: 1` (mismo valor que su hermano `#reconocer-section.shine-card`,
  // ver `recognition.css`), así que ambos son stacking contexts empatados en z-index — gana el
  // que aparece DESPUÉS en el DOM. Como `#reconocer-section` viene después, su contenido se pinta
  // por encima del contenido entero de `campaign-docs-section`, incluido `.pdf-overlay` aunque
  // sea `position: fixed` con `z-index: 2000`: ese z-index solo compite dentro del stacking context
  // del padre, nunca contra hermanos del padre (mismo patrón que `PostItDetailModal.tsx`). Sin
  // portal el modal quedaba visualmente detrás del formulario de "Reconoce a tus compañeros".
  return createPortal((
    <div className="pdf-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      {/* Visor con la maquetación del prototipo (`.pdf-overlay`/`.pdf-modal`/-header/-title/
          -actions/-download/-close/-iframe, demo ShineTogetherHomePage.tsx : 429-462). Sustituye
          al `.bj-pdf-viewer` heredado de `BrillamosJuntosPage`, que es una pantalla que el
          prototipo no usa en el menú lateral. Comportamiento intacto: mismas pestañas por idioma,
          misma descarga, mismo cierre por Escape/click fuera. */}
      <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-header">
          <div className="pdf-modal-title">
            <span className="pdf-modal-icon" aria-hidden="true">{catIcon(group.categoria)}</span>
            <span>{title}</span>
          </div>
          <div className="pdf-modal-actions">
            {/* Selector de idioma: solo el integrado (documentos multi-idioma reales vía API);
                el prototipo tiene un PDF fijo por documento — restilado al mismo lenguaje visual. */}
            {group.variants.length > 1 && (
              <div className="bj-doc-tabs" role="tablist">
                {group.variants.map((d, i) => (
                  <button
                    key={d.id}
                    type="button"
                    role="tab"
                    aria-selected={i === tab}
                    className={`bj-doc-tab ${i === tab ? 'bj-doc-tab--on' : ''}`}
                    onClick={() => setTab(i)}
                  >
                    {d.languageCode === COMODIN_IDIOMA
                      ? t('docs.allLanguages')
                      : d.languageCode.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
            <a className="pdf-modal-download" href={current.url} download={current.fileName ?? undefined} target="_blank" rel="noreferrer" aria-label={t('docs.download')}>
              <i className="fas fa-download" aria-hidden="true" /> <span>{t('docs.download')}</span>
            </a>
            <button type="button" className="pdf-modal-close" onClick={onClose} aria-label={t('common.close')}>✕</button>
          </div>
        </div>

        {loadError
          ? <p className="pdf-modal-error" role="alert">{t('docs.loadError')}</p>
          : <PdfCanvasViewer key={current.id} url={current.url} onError={() => setLoadError(true)} />}
      </div>
    </div>
  ), document.body);
}

/** Agrupa documentos por categoría y ordena cada grupo por `orden` (orden de carga). Pura: mismo
 *  criterio tanto para documentos de campaña como de excepción — ambos son `CampaignDocumentDto`. */
export function groupByCategoria(items: readonly CampaignDocumentDto[]): DocGroup[] {
  const byCat = new Map<string, CampaignDocumentDto[]>();
  for (const d of items) {
    const list = byCat.get(d.categoria) ?? [];
    list.push(d);
    byCat.set(d.categoria, list);
  }
  return Array.from(byCat.entries()).map(([categoria, variants]) => ({
    categoria,
    variants: [...variants].sort((a, b) => a.orden - b.orden),
  }));
}

/**
 * R8b — decide la fuente entre EXCEPCIÓN y CAMPAÑA (niveles 1 y 2). No conoce corporativos (nivel 4,
 * vive en `pickFinalDocGroups`) ni los estáticos (piso absoluto, retirado).
 *
 * OVERRIDE DURO (revertido 2026-08-18, ver cabecera del archivo): con excepción ACTIVA, la excepción
 * es SIEMPRE la fuente — con documentos propios o sin ellos. Ya NO cae a `campaignGroups` cuando la
 * excepción está vacía: antes sí (Tarea 15 original), pero eso hacía que una excepción activa sin
 * documentos mostrara documentos de una campaña que no corresponden a esa OU. `campaignGroups` solo
 * se usa cuando `hasActiveException` es `false`.
 */
export function pickDocGroups(
  hasActiveException: boolean,
  exceptionGroups: DocGroup[],
  campaignGroups: DocGroup[],
): DocGroup[] {
  if (hasActiveException) return exceptionGroups;
  return campaignGroups;
}

/**
 * WP-08/RF-079 — mapeo 1:1 entre la categoría de documento de campaña (`DOC_CATEGORIES`,
 * `admin/adminShared.tsx`) y el `CorporateDocType` del backend de UserAdminService-api. Vocabulario
 * distinto por historia (uno nació con la landing de campañas, el otro con el repositorio corporativo
 * §11), pero la correspondencia es la misma familia de documento en los dos catálogos.
 */
export const CATEGORY_TO_CORPORATE_TYPE: Record<string, CorporateDocType> = {
  howto: 'GuiaUsuario',
  awards: 'Premios',
  legal: 'BasesLegales',
  privacy: 'PoliticaPrivacidad',
  cookies: 'CookiesPolicy',
  notice: 'LegalNotice',
};

const CATEGORY_BY_CORPORATE_TYPE: Partial<Record<CorporateDocType, string>> = Object.fromEntries(
  Object.entries(CATEGORY_TO_CORPORATE_TYPE).map(([categoria, tipo]) => [tipo, categoria]),
);

/**
 * Tarea 16 — convierte los documentos corporativos (`GET v1/corporate-documents/for-me?lang=`, YA
 * filtrados por la OU del usuario en el backend) a la misma forma `DocGroup` que usan campaña y
 * excepción, reutilizando `groupByCategoria` (mismo criterio de agrupación en los tres orígenes).
 * `for-me` recibe `lang` y devuelve la URL ya resuelta para ese idioma, así que cada documento aporta
 * una única variante — sin selector de idioma en el visor, a diferencia de campaña/excepción.
 */
export function corporateDocGroups(
  items: readonly CorporateDocumentForMeDto[],
  lang: string,
): DocGroup[] {
  const asDocuments: CampaignDocumentDto[] = items
    .filter((corp) => Boolean(corp.url))
    .map((corp) => ({
      id: corp.id,
      campaignId: '',
      categoria: CATEGORY_BY_CORPORATE_TYPE[corp.tipo] ?? corp.tipo.toLowerCase(),
      languageCode: lang,
      titulo: { [lang]: corp.nombre },
      url: corp.url as string,
      fileName: corp.fileName,
      orden: 0,
    }));
  return groupByCategoria(asDocuments);
}

/**
 * R8b — Tarea 16, nivel 4. Envuelve `pickDocGroups` (niveles 1-2) y agrega el fallback final:
 * documentos CORPORATIVOS globales cuando la campaña (SIN excepción activa) tampoco aporta nada. Si
 * tampoco hay corporativos, devuelve `[]` — la sección se oculta por completo (ver cabecera del
 * archivo; ya no cae al piso estático de 6 PDFs genéricos de la decisión de 2026-08-03, revertida).
 *
 * OVERRIDE DURO (2026-08-18): con excepción ACTIVA nunca cae a corporativos, tenga o no documentos
 * propios — `pickDocGroups` ya no cae a `campaignGroups` en ese caso (ver su comentario), y este
 * nivel 4 tampoco debe rescatar ese vacío con el repositorio genérico: sería el mismo problema
 * (mostrar documentos que no son de esa OU) un nivel más abajo.
 *
 * Decisión de negocio YA CONFIRMADA (Tarea 16): invierte a propósito la regla de "repositorios
 * separados" que documenta `CorporateDocument.cs` (RN-G10/RN-E06) — es intencional.
 */
export function pickFinalDocGroups(
  hasActiveException: boolean,
  exceptionGroups: DocGroup[],
  campaignGroups: DocGroup[],
  corporateGroups: DocGroup[],
): DocGroup[] {
  const resolved = pickDocGroups(hasActiveException, exceptionGroups, campaignGroups);
  if (hasActiveException) return resolved;
  if (resolved.length > 0) return resolved;
  return corporateGroups;
}

export function CampaignDocs({
  campaignId, programa, isPersonalizada = false, allowCorporateFallback = true,
}: Readonly<{
  campaignId: string | undefined;
  programa?: CampaignType;
  isPersonalizada?: boolean;
  /**
   * BUG (QA, 2026-08-23) — reportado en `/app/brillamos`: al borrar los documentos de la campaña,
   * la sección seguía mostrando 2 tarjetas ("como si fueran un default"), sin que QA supiera de
   * dónde salían. NO eran datos corruptos ni un hardcode: es el nivel 4 (documentos CORPORATIVOS
   * de la OU, decisión de negocio ya confirmada — Tarea 16) funcionando como estaba diseñado. El
   * cliente decidió, al enterarse, que para Brillamos Juntos esto NO es lo que quiere: sin
   * documentos propios de la campaña, la sección debe ocultarse por completo, igual que ya pasa
   * con una excepción activa sin documentos propios (nivel 2) o con una campaña Personalizada
   * (`isPersonalizada`). `BrillamosView.tsx` es el único llamador que lo apaga hoy;
   * `InnovacionView.tsx` no lo necesita porque `isPersonalizada` ya lo desactiva ahí. Default
   * `true` a propósito: no cambia el comportamiento de ningún otro llamador existente.
   */
  allowCorporateFallback?: boolean;
}>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const docs = useCampaignDocuments(campaignId);

  // R8b — «excepción ACTIVA para la OU del usuario». Lo resuelve el SERVIDOR
  // (`GET v1/campaign-exceptions/active-for-me`) a partir de la identidad del llamador, que es el
  // mismo patrón que ya usan los seis consumidores de runtime del servicio
  // (`GetLandingBannerQueryHandler`, `BrillamosScheduleGate`, `LocalityVisibilityResolver`,
  // `CreateRecognitionCommandHandler`, `LocalityExceptionEnforcement`,
  // `ConfigRecognitionPolicyProvider`).
  //
  // 🔴 Antes se resolvía aquí, filtrando el LISTADO de administración
  // (`useCampaignExceptions({ activa: true, pageSize: 100 })`) por `alcance.includes(ou)`. Dos
  // problemas, los dos medidos en QA:
  //
  //  1. Ese listado exige la capacidad `CrearEditarCampanas`, así que respondía **403 a cualquier
  //     usuario no admin** — corporativo incluido. Con el 403, `activeException` quedaba en null y la
  //     cascada caía al nivel 3/4: el colaborador veía los documentos de la campaña o los
  //     corporativos en vez de los de la excepción de SU OU, sin ningún aviso.
  //  2. El listado con `activa=true` **no filtra por estado de aprobación**, así que podía elegir una
  //     excepción reabierta a `pendiente` (editar una aprobada la reabre) que siguiera `Activa`. El
  //     endpoint nuevo exige `aprobada`, que es la semántica que aplica el resto del servicio.
  //
  // El endpoint devuelve sólo `id` y `tipo`: los metadatos de administración y auditoría de la
  // excepción no viajan a una pantalla de usuario.
  const exceptionQuery = useActiveCampaignException(programa);
  const activeException = exceptionQuery.data ?? null;
  const exceptionsEnabled = Boolean(programa);
  const exceptionDocs = useCampaignExceptionDocuments(activeException?.id);

  // Niveles 1-2 (override duro, revertido 2026-08-18 — ver cabecera del archivo): con excepción
  // activa, la excepción manda siempre, con o sin documentos propios; `campaignGroups` solo entra en
  // juego cuando NO hay excepción activa (nivel 3).
  const campaignGroups = useMemo(() => groupByCategoria(docs.data ?? []), [docs.data]);
  const exceptionGroups = useMemo(() => groupByCategoria(exceptionDocs.data ?? []), [exceptionDocs.data]);
  const primaryGroups = useMemo(
    () => pickDocGroups(activeException != null, exceptionGroups, campaignGroups),
    [activeException, exceptionGroups, campaignGroups],
  );

  // Nivel 4 (Tarea 16) — corporativos globales. Se pide SOLO cuando los niveles 1-3 ya resolvieron
  // (docs/excepción/documentos de excepción, cada uno según si aplica) y aun así no aportaron nada:
  // evita una llamada de más en el caso común (la campaña sí trae documentos propios).
  // EXCLUSIÓN Personalizada (2026-08-18) — una campaña por localidad sin documentos propios debe
  // ocultar la sección, no mostrar el repositorio corporativo genérico: el corporativo no es
  // específico de esa localidad, y mostrarlo confunde al usuario sobre qué cargó. El nivel 4 sigue
  // aplicando a Fundamentales/Brillamos, donde sí tiene sentido como respaldo global del programa.
  // Prop DEDICADA `isPersonalizada`, NO se deriva de `programa`: `BrillamosView.tsx` pasa
  // `programa="Brillamos"` FIJO incluso para su landing REQ-115 de campaña Personalizada (porque las
  // excepciones de "Campañas por OU" se resuelven por programa, nunca por tipo de campaña — ver
  // comentario en ese archivo), así que `programa` nunca vale 'Personalizada' en ese call site pese a
  // que la campaña sí lo es. Corregido tras verificar en Mongo (OU FOSE) que el primer fix, basado en
  // `programa !== 'Personalizada'`, no aplicaba ahí y seguía cayendo al corporativo.
  // Override duro (2026-08-18): con excepción ACTIVA nunca se pide el nivel 4 — ni con documentos
  // propios (ya resuelve en nivel 1, `primaryGroups.length > 0`) ni sin ellos (nivel 2 ya no cae a
  // nada rescatable, `pickFinalDocGroups` lo ignoraría de todas formas). Evita una llamada de red que
  // nunca se va a usar.
  const allowsCorporateFallback = allowCorporateFallback && !isPersonalizada && activeException == null;
  const primarySettled = (!campaignId || docs.isFetched)
    && (!exceptionsEnabled || exceptionQuery.isFetched)
    && (!activeException || exceptionDocs.isFetched);
  const needsCorporateFallback = allowsCorporateFallback && primarySettled && primaryGroups.length === 0;
  const corporateQuery = useCorporateDocumentsForMe(lang, needsCorporateFallback);

  const [openCat, setOpenCat] = useState<string | null>(null);

  const groups: DocGroup[] = useMemo(() => {
    // `allowsCorporateFallback` se revisa aquí también, no solo en el `enabled` de la query: la
    // query de corporativos comparte queryKey (solo por `lang`) con la de cualquier otra pantalla
    // del mismo idioma, así que un dato ya cacheado por una campaña Fundamentales/Brillamos podría
    // llegar igual a `corporateQuery.data` aunque esta instancia la haya pedido con `enabled: false`.
    const corporateGroups = allowsCorporateFallback
      ? corporateDocGroups(corporateQuery.data ?? [], lang)
      : [];
    return pickFinalDocGroups(activeException != null, exceptionGroups, campaignGroups, corporateGroups);
  }, [activeException, exceptionGroups, campaignGroups, corporateQuery.data, lang, allowsCorporateFallback]);

  // Mientras se resuelve CUALQUIER nivel no se pinta nada — mismo criterio de "no enseñar y sustituir
  // un instante después" en los 4 niveles, sin excepción para el corporativo (revisión de la Tarea
  // 16): sin este bloqueo, una OU CON documentos corporativos configurados vería primero la sección
  // vacía y, un instante después, cambiaría sola a los reales — sin aviso. Sin `programa`/`ou` la
  // consulta de excepciones ni siquiera arranca (`exceptionsEnabled`), así que ese caso no bloquea;
  // lo mismo para corporativos cuando los niveles previos ya resolvieron con documentos propios
  // (`needsCorporateFallback` en false).
  if (campaignId && docs.isLoading) return null;
  if (exceptionsEnabled && exceptionQuery.isLoading) return null;
  if (activeException && exceptionDocs.isLoading) return null;
  if (needsCorporateFallback && corporateQuery.isLoading) return null;
  // Ningún nivel aportó documentos (ni excepción, ni campaña, ni corporativos): la sección se oculta
  // por completo en vez de mostrar contenido genérico que no es de esta OU/campaña.
  if (groups.length === 0) return null;

  const activeGroup = groups.find((g) => g.categoria === openCat) ?? null;

  return (
    // Maquetación del prototipo (`.campaign-docs-section`/-title/-grid + `.campaign-doc-card`
    // con icono a la IZQUIERDA y etiqueta al lado, demo ShineTogetherHomePage.tsx : 402-426).
    // El namespace `.bj-docs*` venía de `BrillamosJuntosPage`, que apilaba icono sobre texto.
    <section className="campaign-docs-section">
      <h3 className="campaign-docs-title"><span aria-hidden="true">📁</span> {t('docs.title')}</h3>
      <div className="campaign-docs-grid">
        {groups.map((g) => (
          <button key={g.categoria} type="button" className="campaign-doc-card shine-card" onClick={() => setOpenCat(g.categoria)}>
            <span className="campaign-doc-icon" aria-hidden="true">{catIcon(g.categoria)}</span>
            <span className="campaign-doc-label">{t(`docs.cat.${g.categoria}`, g.categoria)}</span>
            {g.variants.length > 1 && <span className="campaign-doc-langs">{g.variants.length} <i className="fas fa-language" /></span>}
          </button>
        ))}
      </div>
      {activeGroup && <DocViewer group={activeGroup} lang={lang} onClose={() => setOpenCat(null)} />}
    </section>
  );
}

export default CampaignDocs;
